import React from "react";
import { Check, Circle, AlertTriangle } from "lucide-react";
import { cn } from "../../lib/utils";

const STAGE_ORDER = ["NOT_READY", "READY", "CALCULATED", "SUBMITTED_FOR_REVIEW", "RETURNED_FOR_CORRECTION", "REVIEWED_APPROVED", "PUBLISHED"];

/** 7-stage payroll lifecycle stepper (Payroll & Leave Deep Audit item 4). Each stage shows its
 * real status/actor/date pulled directly from the period document - never a generic progress
 * bar - so a reviewer/publisher can see exactly who did what and when without leaving this page.
 * Stages can never be skipped: every action button downstream is already gated server-side by
 * `payroll_service` status checks (see PayrollPeriodDetailPage's action buttons). */
export function PayrollLifecycleStepper({ period, paymentsSummary, testId }) {
  const status = period.status;
  const hasReadiness = !!period.readiness_report;
  const allReady = hasReadiness && period.readiness_report.every((r) => r.ready);
  const calculated = ["CALCULATED", "SUBMITTED_FOR_REVIEW", "RETURNED_FOR_CORRECTION", "REVIEWED_APPROVED", "PUBLISHED"].includes(status);
  const submittedForReview = ["SUBMITTED_FOR_REVIEW", "REVIEWED_APPROVED", "PUBLISHED"].includes(status);
  const reviewed = ["REVIEWED_APPROVED", "PUBLISHED"].includes(status);
  const published = status === "PUBLISHED";
  const paymentsDone = published && paymentsSummary && paymentsSummary.total > 0 && paymentsSummary.confirmed === paymentsSummary.total;

  const stages = [
    {
      key: "readiness", label: "Readiness", role: "Preparer",
      state: !hasReadiness ? "pending" : allReady ? "done" : "blocked",
      detail: !hasReadiness ? "Not yet run" : allReady ? "All employees ready" : `${period.readiness_report.filter((r) => !r.ready).length} blocked`,
    },
    { key: "calculate", label: "Calculate", role: "Preparer", state: calculated ? "done" : "pending", detail: calculated ? period.calculated_at : "Awaiting calculation", actor: period.prepared_by },
    { key: "preparer_review", label: "Preparer Review", role: "Preparer", state: submittedForReview ? "done" : status === "RETURNED_FOR_CORRECTION" ? "blocked" : "pending", detail: status === "RETURNED_FOR_CORRECTION" ? `Returned: ${period.return_reason || "see notes"}` : submittedForReview ? "Submitted for review" : "Awaiting submission" },
    { key: "reviewer_approval", label: "Reviewer Approval", role: "Reviewer", state: reviewed ? "done" : "pending", detail: reviewed ? period.reviewed_at : "Awaiting review", actor: period.reviewed_by },
    { key: "publish", label: "Publish", role: "Publisher", state: published ? "done" : "pending", detail: published ? period.published_at : "Awaiting publish", actor: period.published_by },
    { key: "payment", label: "Payment Confirmation", role: "Payroll/Finance", state: !published ? "pending" : paymentsDone ? "done" : "in_progress", detail: !published ? "Awaiting publish" : paymentsSummary ? `${paymentsSummary.confirmed}/${paymentsSummary.total} confirmed` : "See Payment Evidence" },
    { key: "delivery", label: "Employee Delivery", role: "System", state: published ? "done" : "pending", detail: published ? "Payslips visible in employee portal" : "Awaiting publish" },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid={testId}>
      <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">Payroll Lifecycle</p>
      <div className="flex flex-col md:flex-row gap-2 md:gap-0">
        {stages.map((s, i) => (
          <div key={s.key} className="flex-1 flex md:flex-col items-start md:items-center gap-2 md:gap-1.5 relative" data-testid={`${testId}-stage-${s.key}`}>
            <div className="flex items-center md:flex-col gap-2 w-full">
              <div className={cn(
                "shrink-0 h-7 w-7 rounded-full flex items-center justify-center border-2",
                s.state === "done" && "bg-emerald-600 border-emerald-600 text-white",
                s.state === "in_progress" && "bg-blue-100 border-blue-400 text-blue-700",
                s.state === "blocked" && "bg-red-100 border-red-400 text-red-700",
                s.state === "pending" && "bg-gray-50 border-gray-300 text-gray-500",
              )}>
                {s.state === "done" ? <Check size={15} /> : s.state === "blocked" ? <AlertTriangle size={13} /> : <Circle size={9} fill="currentColor" />}
              </div>
              {i < stages.length - 1 && <div className="hidden md:block flex-1 h-px bg-gray-200 mt-0" />}
              <div className="md:hidden flex-1">
                <p className="text-xs font-semibold text-gray-800">{s.label}</p>
                <p className="text-[11px] text-gray-500">{s.role} · {s.detail}{s.actor ? ` · ${s.actor}` : ""}</p>
              </div>
            </div>
            <div className="hidden md:block text-center">
              <p className="text-xs font-semibold text-gray-800">{s.label}</p>
              <p className="text-[10px] text-gray-500">{s.role}</p>
              <p className="text-[10px] text-gray-500 mt-0.5 max-w-[110px] truncate" title={s.detail}>{s.detail}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
